import { usePickslipStore } from "../stores/pickslipStore";
import { getBettingChannel } from "@/utilities/bettingChannels";
import { getHoursUntil } from "@/utilities/datetime";
import {
  totalPrizePoolAsDouble,
  parsePointsEntered,
} from "@/features/pickems/util/helpers";
import { message } from "@/utilities/message/message";
import { telemetry } from "@/utilities/telemetry";
import { triggerHaptic } from "@/utilities/haptics";
import type { PickemsContestWithMarkets } from "@/hooks/firestore/pickems/usePickemsContest";
import type { PickslipPick } from "../types/PickslipPick";
import type { NullableMaybe } from "types/utilities";
import type { ProcessedPickemEntry } from "@/hooks/firestore/betting/useEntries";

const createAnalyticsData = (
  contest: PickemsContestWithMarkets,
  entry: NullableMaybe<ProcessedPickemEntry>,
  picks: PickslipPick[],
  potentialPoints: number,
) => {
  const scheduledStartTime = contest.scheduledStartTime;
  const hoursBeforeStart = Math.round(getHoursUntil(scheduledStartTime ?? ""));

  const payload = {
    Competitors: picks.flatMap((x) =>
      x.competitor?.name ? [x.competitor.name] : [],
    ),
    ContestId: contest.id,
    Currency: contest.currency || "AUD",
    EntryCount: contest.entries?.total || 0,
    EntryFee: 0,
    HoursBeforeStart: hoursBeforeStart,
    MarketCount: contest.marketCounts.total,
    PayoutPlacesCount: contest.prizePool.length,
    PointsPotential: potentialPoints,
    PrizePool: totalPrizePoolAsDouble(contest?.prizePool),
    ScheduledStartTime: scheduledStartTime?.toISOString(),
    SelectionCount: picks.length,
    Sports: contest?.sports,
    Title: contest.title,
  };

  const isUpdate = !!entry;

  return { payload, isUpdate };
};

export const submitPicks = async (
  contest: PickemsContestWithMarkets,
  entry: NullableMaybe<ProcessedPickemEntry>,
) => {
  const store = usePickslipStore.getState();
  const potentialPoints = parsePointsEntered(
    contest,
    entry?.data?.selections,
    store.picks,
  );

  if (store.confirmContestId !== contest.id) {
    store.updatePickslip({ confirmContestId: contest.id, justAccepted: false });
    return;
  }

  store.setIsLoading(true);
  store.setError(null);

  try {
    const contestPicks = store.picks.filter((x) => x.sourceId === contest.id);
    const selections = contestPicks.map((x) => ({
      outcomeId: x.outcomeId,
      points: Number(x.odds) || 0,
    }));

    const payload = {
      contestId: contest.id,
      selections,
      channel: getBettingChannel(),
      currency: contest.currency || "AUD",
    };

    const analyticsData = createAnalyticsData(
      contest,
      entry,
      store.picks,
      potentialPoints,
    );

    const [response, error] = await message(
      "pickems-submit-selection",
      payload,
    );

    if (error) {
      const errorMessage = error.errors?.[0] || error;
      store.setError(errorMessage.message);
      throw errorMessage;
    }

    if (response?.status === "SUCCESS") {
      const entryId = response.data?.entryId;
      const eventPayload = { ...analyticsData.payload, EntryId: entryId };

      if (analyticsData.isUpdate) {
        telemetry.entryUpdated(eventPayload);
      } else {
        telemetry.entrySubmitted(eventPayload);
      }

      triggerHaptic.success();

      if (!entry) {
        store.updatePickslip({ pickAcceptedId: entryId, justAccepted: true });
      } else {
        store.updatePickslip({ pickAcceptedId: entryId });
      }
    }
  } catch (error) {
    store.updatePickslip({
      pickAcceptedId: null,
      confirmContestId: null,
    });
    triggerHaptic.error();
    throw error;
  } finally {
    store.setIsLoading(false);
  }
};
